import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ListHeaderCloseButton,
  DebouncedSearchInput,
  ListHeaderSearchButton,
  useScreenWidth,
} from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { difference } from "lodash-es";
import React from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useLatest } from "react-use";
import {
  Agreement,
  namedOperations,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageColumn } from "../../commons/layout/PageColumn.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { ButtonItemBindingSelectMultiple } from "../../features/doc-items/fields/ButtonItemBindingSelectMultiple.js";
import { ButtonItemTypeSelectMultiple } from "../../features/doc-items/fields/ButtonItemTypeSelectMultiple.js";
import {
  getViewerDecisionRole,
  isTreePreviewItemVisible,
} from "../../features/doc-items/helpers.js";
import type { ItemChange } from "../../features/doc-items/hooks/useItemBindings.js";
import { useItemsSearch } from "../../features/doc-items/hooks/useItemsSearch.js";
import { ItemSearchChips } from "../../features/doc-items/ItemSearchChips.js";
import {
  createTreeClientItem,
  createTreeItem,
  createTreeItemInput,
} from "../../features/projects/quote-trees.js";
import {
  allowHaveChildren,
  shouldRenderCreateInput,
} from "../../features/quotes/helpers.js";
import { QuoteAction } from "../../features/quotes/QuoteAction.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useCreateQuoteItems } from "../../features/quotes/useCreateQuoteItems.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { ThreeD } from "../../features/threeD/ThreeD.js";
import { ThreeDEditorRef } from "../../features/threeD/ThreeDEditor.js";
import { VirtualBareTreeStandaloneItem } from "../../trees-virtual/components/VirtualBareTreeStandaloneItem.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../trees-virtual/hooks/useExpandedStore.js";
import { TreeViewMode } from "../../trees-virtual/types.js";
import { VirtualItemTree } from "../../trees-virtual/VirtualItemTree.js";
import { TreeToggleAllExpandedButton } from "../../trees/components/TreeToggleButton.js";
import { falseFn, nullFn } from "../../utils.js";
import { useProjectQuoteContext } from "./ProjectQuoteContext.js";
import { ProjectQuoteEdit_ItemFragment } from "./ProjectQuoteEdit.generated.js";
import {
  ProjectQuoteThreeD_ItemFragment,
  useProjectQuoteThreeDQuery,
} from "./ProjectQuoteThreeD.generated.js";

const REFETCH_QUERIES = [namedOperations.Query.ProjectQuoteEdit];

interface Props {
  projectId: string;
  quoteId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export function ProjectQuoteThreeD({
  projectId,
  quoteId,
  submenuItems,
  prefixBreadcrumbs,
  activeSubmenuItem,
}: Props) {
  const { t } = useTranslate(["Global", "Quote", "ProjectOverview"]);

  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get("r");
  const { itemId: selectedItemId = null } = useParams<{
    projectId: string;
    quoteId: string;
    itemId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const latestLocation = useLatest(location);
  const viewer = useUserData().currentUser!;

  const threeDEditorRef = React.useRef<ThreeDEditorRef>(null);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const isClientVisibilityEnabled = useFeature("ClientVisibility");

  const { isMinDesktop, isMinTablet } = useScreenWidth();

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote",
    quoteId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useProjectQuoteThreeDQuery({
    client,
    variables: {
      projectId,
      quoteId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });
  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.quote)?.quote;
  const docTitle = doc?.title ?? "";

  const pathToDocList = `/projects/${projectId}/quotes`;
  const pathToDoc = `/projects/${projectId}/quotes/${quoteId}`;
  const pathToDocPage = `${pathToDoc}/3d`;

  const isAgreed =
    (doc?.isPublished && doc?.isBinding && doc?.agreement === "YES") ?? false;
  const isViewersQuote =
    doc?.owningSystemOrganisationId === viewer.organisation.id;

  const enableCreating = isViewersQuote ? true : false;
  const enableDragging = isViewersQuote ? !isAgreed : false;

  const {
    filterItemSearch,
    filters,
    setFilters,
    resetFilters,
    search,
    setSearch,
    searchRef,
    toggleSearchVisible,
    searchVisible,
    isSearchOrFiltersEnabled,
    parentItemIds,
    isItemMuted,
  } = useItemsSearch({
    projectId,
    docId: quoteId,
    // when search is getting applied - we need to open tree to all found items
    onSearchApplied: ({ parentItemIds }) => {
      if (
        difference(parentItemIds, expandedStore.expandedItemIds ?? []).length >
        0
      ) {
        expandedStore.setItemsExpanded(parentItemIds, true);
      }
    },
  });

  const showExpandButton = React.useCallback(
    (item: ProjectQuoteThreeD_ItemFragment) => {
      if (!isSearchOrFiltersEnabled) return item.hasChildren;
      return item.hasChildren && parentItemIds.includes(item.id);
    },
    [isSearchOrFiltersEnabled, parentItemIds]
  );

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems?.filter(
      item => !item.isAnyParentItemEliminated
    );
    const filteredItems = treeItems.filter(filterItemSearch);
    return [filteredItems, rootItem, allDocItems];
  }, [doc, filterItemSearch]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  const isRootSelected = selectedItemId === rootItem?.id;

  const navigateToItem = React.useCallback(
    (id: string) =>
      navigate(
        {
          pathname: `${pathToDocPage}/items/${id}`,
          search: latestLocation.current.search,
        },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );
  const navigateToDocPage = React.useCallback(
    () =>
      navigate(
        { pathname: pathToDocPage, search: latestLocation.current.search },
        { replace: true }
      ),
    [latestLocation, navigate, pathToDocPage]
  );

  const quote = getDataOrNull(query.data?.quote)?.quote;

  const viewerPermissions: PermissionName[] = React.useMemo(
    () => quote?.viewerPermissions ?? [],
    [quote?.viewerPermissions]
  );
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const agreement: Agreement | undefined = doc?.agreement;

  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const { selectWizardContextButton, refetchWizardQuestions } =
    useProjectQuoteContext();

  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  } = useCreateQuoteItems({
    projectId,
    quoteId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated: refetchWizardQuestions,
    source: "3d",
  });

  const TreeItem = React.useMemo(() => {
    if (!quote?.id || agreement === undefined) return nullFn;

    return isViewersQuote
      ? createTreeItem({
          pathToDocPage,
          projectId,
          doc: {
            id: quote.id,
            rootItemId: quote?.rootItem.id,
            viewerPermissions,
            owningSystemOrganisationId: quote.owningSystemOrganisationId,
          },
          docAgreement: agreement,
          startConfirmProcess,
          expandedItemIds: expandedStore.expandedItemIds,
          setItemExpanded: expandedStore.setItemExpanded,
          pasteItem,
          isAllowedToPasteItem,
          onAdditionalItemInputChange: null,
          getTo: itemProps => ({
            pathname: `${pathToDocPage}/items/${itemProps.item.id}`,
            search: latestLocation.current.search,
          }),
          isItemMuted: itemProps => isItemMuted(itemProps.item),
        })
      : createTreeClientItem({
          projectId,
          doc: {
            id: quote.id,
            rootItemId: quote?.rootItem.id,
            viewerPermissions,
            owningSystemOrganisationId: quote.owningSystemOrganisationId,
          },
          docAgreement: agreement,
          expandedItemIds: expandedStore.expandedItemIds,
        });
  }, [
    quote?.id,
    quote?.rootItem.id,
    quote?.owningSystemOrganisationId,
    agreement,
    isViewersQuote,
    pathToDocPage,
    projectId,
    viewerPermissions,
    startConfirmProcess,
    expandedStore.expandedItemIds,
    expandedStore.setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    isItemMuted,
    latestLocation,
  ]);

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        viewMode: TreeViewMode.Item,
        quoteId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      quoteId,
    ]
  );

  // redirect to edit route when selected item is filtered out
  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigateToDocPage();
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    pathToDocPage,
    rootItem,
    navigateToDocPage,
    query.loading,
  ]);

  const filterItem = React.useCallback(
    (i: ProjectQuoteThreeD_ItemFragment) =>
      isTreePreviewItemVisible(true, i, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <QuoteSubHeader
            project={project}
            quote={doc}
            activeView="3d"
            canEdit={isViewersQuote}
            isReadOnly={isAgreed}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={expandedStore.expandedItemIds}
          />
        ) : undefined
      }
      action={
        project &&
        doc && (
          <QuoteAction
            doc={doc}
            project={project}
            onDiscardPendingChangesRefetchQueries={REFETCH_QUERIES}
            onDeclineChangesRefetchQueries={REFETCH_QUERIES}
            viewerDecisionRole={viewerDecisionRole}
            expandedItemIds={expandedStore.expandedItemIds}
          />
        )
      }
    >
      {isMinDesktop && (
        <PageColumn
          $hasRightBorder
          $hasBackground
          $display="block"
          ref={isMinTablet ? setTreeContainer : undefined}
        >
          {doc && project && rootItem && (
            <Stack direction={"column"} spacing={2}>
              <Stack
                direction="row"
                width="100%"
                alignItems="center"
                spacing={1}
              >
                <TreeToggleAllExpandedButton
                  areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                  setAllItemsExpanded={expandedStore.setAllItemsExpanded}
                />
                <ButtonItemTypeSelectMultiple
                  value={filters.type ?? []}
                  onChange={value => {
                    setFilters(f => ({
                      ...f,
                      type: value.length > 0 ? value : undefined,
                    }));
                  }}
                />
                <ButtonItemBindingSelectMultiple
                  value={[
                    ...(filters.binding ?? []),
                    ...(filters.needsAgreement ? ["change" as ItemChange] : []),
                  ]}
                  onChange={value => {
                    const bindings = value.filter(v => v !== "change");
                    const needsAgreement = value.some(v => v === "change");
                    setFilters(f => ({
                      ...f,
                      binding: bindings.length > 0 ? bindings : undefined,
                      needsAgreement: needsAgreement || undefined,
                    }));
                  }}
                  hasAgreement={doc?.agreement === "YES"}
                />
                {isViewersQuote ? selectWizardContextButton : null}
                {!searchVisible && (
                  <ListHeaderSearchButton onClick={toggleSearchVisible} />
                )}
              </Stack>

              <ItemSearchChips
                filters={filters}
                setFilters={setFilters}
                onReset={resetFilters}
              />

              {searchVisible && (
                <Stack
                  direction="row"
                  width="100%"
                  alignItems="center"
                  spacing={1}
                >
                  <DebouncedSearchInput
                    inputRef={searchRef}
                    placeholder={t("Search", { ns: "Global" })}
                    defaultValue={search}
                    onChangeSearchTerm={setSearch}
                  />
                  <ListHeaderCloseButton onClick={toggleSearchVisible} />
                </Stack>
              )}

              <Box>
                <VirtualBareTreeStandaloneItem
                  item={rootItem}
                  items={filteredTreeItems}
                  allItems={allDocItems}
                  depth={0}
                  itemComponent={TreeItem}
                  selected={isRootSelected}
                  sticky={isRootSelected}
                  top={0}
                  clickable
                  onClick={() => navigateToItem(rootItem.id)}
                />

                <VirtualItemTree<
                  ProjectQuoteThreeD_ItemFragment,
                  typeof enableDragging
                >
                  docId={enableDragging ? doc.id : null}
                  projectId={enableDragging ? projectId : null}
                  items={filteredTreeItems}
                  allItems={allDocItems}
                  filterItem={
                    isClientVisibilityEnabled || !isViewersQuote
                      ? filterItem
                      : undefined
                  }
                  selectedItemId={selectedItemId}
                  enableCreating={enableCreating}
                  enableDragging={enableDragging}
                  documentItemTypes={QUOTE_ITEM_TYPES}
                  container={treeContainer}
                  itemComponent={TreeItem}
                  inputComponent={isViewersQuote ? TreeItemInput : nullFn}
                  allowHaveChildren={
                    isViewersQuote ? allowHaveChildren : falseFn
                  }
                  shouldRenderCreateInput={
                    isViewersQuote ? shouldRenderCreateInput : falseFn
                  }
                  expandedStore={expandedStore}
                  showExpandButton={showExpandButton}
                />
              </Box>
            </Stack>
          )}
        </PageColumn>
      )}
      <ConfirmProcess ref={confirmProcessRef} />

      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole,
        }}
      />

      <PageContainer $fullHeight $noPadding>
        <ThreeD
          threeDEditorRef={threeDEditorRef}
          projectId={projectId}
          docId={quoteId}
          returnPath={returnPath ?? pathToDoc}
          allDocItems={allDocItems}
          isEditable={isMinDesktop}
        />
      </PageContainer>
    </Page>
  );
}
